/**
 * ButtonEditor Component
 *
 * Shared component for editing CTA buttons in marketing blocks
 * Supports up to 2 buttons with primary/secondary styles
 *
 * Features:
 * - Button text input (max 50 chars)
 * - URL input with validation
 * - Style selection (primary/secondary)
 * - Add/remove buttons (max 2)
 * - Preview in view mode
 *
 * Props:
 * - buttons: Array of button objects [{ text, url, style }]
 * - onChange: Callback when buttons array changes
 * - maxButtons: Maximum number of buttons (default 2)
 * - isEditMode: Boolean for edit/view mode
 */

import React from "react";
import { normalizeWebUrl, sanitizeUrl } from "../../../../utils/sanitize";

const DEFAULT_BUTTON = {
  text: "",
  url: "",
  style: "primary",
};

export default function ButtonEditor({
  buttons = [],
  onChange,
  maxButtons = 2,
  isEditMode = true,
}) {
  if (!isEditMode) {
    if (!buttons || buttons.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-3">
        {buttons.map((button, index) => {
          if (!button.text || !button.url) return null;

          const buttonClasses =
            button.style === "primary"
              ? "bg-fmt-red text-white hover:bg-red-700"
              : "bg-gray-100 text-neutral-900 hover:bg-gray-200 border border-gray-300";

          return (
            <a
              key={index}
              href={sanitizeUrl(button.url)}
              target={button.url.startsWith("http") ? "_blank" : undefined}
              rel={button.url.startsWith("http") ? "noopener" : undefined}
              className={`inline-block px-6 py-3 rounded-lg font-semibold text-center transition-colors ${buttonClasses}`}
            >
              {button.text}
            </a>
          );
        })}
      </div>
    );
  }

  const handleButtonChange = (index, field, value) => {
    const updatedButtons = [...buttons];
    updatedButtons[index] = {
      ...updatedButtons[index],
      [field]: value,
    };
    onChange(updatedButtons);
  };

  const handleAddButton = () => {
    if (buttons.length < maxButtons) {
      onChange([...buttons, { ...DEFAULT_BUTTON }]);
    }
  };

  const handleRemoveButton = (index) => {
    const updatedButtons = buttons.filter((_, i) => i !== index);
    onChange(updatedButtons);
  };

  return (
    <div className="button-editor mb-4">
      <label className="block text-sm font-medium text-neutral-700 mb-2">
        Call-to-Action Buttons
        {buttons.length > 0 && (
          <span className="ml-2 text-xs text-neutral-500">
            ({buttons.length}/{maxButtons})
          </span>
        )}
      </label>

      <div className="space-y-3">
        {buttons.map((button, index) => (
          <div key={index} className="border rounded p-3 bg-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-neutral-600">
                Button {index + 1}
              </span>
              <button
                type="button"
                onClick={() => handleRemoveButton(index)}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                Remove
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* Button text */}
              <div className="col-span-2">
                <input
                  type="text"
                  value={button.text || ""}
                  onChange={(e) =>
                    handleButtonChange(index, "text", e.target.value)
                  }
                  placeholder="Button text (e.g., Get Started)"
                  className="form-control"
                  maxLength={50}
                />
              </div>

              {/* Button URL */}
              <div className="col-span-2">
                <input
                  type="text"
                  value={button.url || ""}
                  onChange={(e) =>
                    handleButtonChange(index, "url", e.target.value)
                  }
                  onBlur={(e) =>
                    handleButtonChange(
                      index,
                      "url",
                      normalizeWebUrl(e.target.value),
                    )
                  }
                  placeholder="https://example.com or /page"
                  className="form-control"
                />
              </div>

              {/* Button style */}
              <div className="col-span-2">
                <select
                  value={button.style || "primary"}
                  onChange={(e) =>
                    handleButtonChange(index, "style", e.target.value)
                  }
                  className="form-control"
                >
                  <option value="primary">Primary (Red Background)</option>
                  <option value="secondary">Secondary (Gray Background)</option>
                </select>
              </div>
            </div>

            {/* Preview */}
            {button.text && (
              <div className="mt-3 pt-3 border-t">
                <div className="text-xs text-neutral-500 mb-2">Preview:</div>
                <button
                  type="button"
                  className={`
                    inline-block px-6 py-3 rounded-lg font-semibold text-center transition-colors
                    ${
                      button.style === "primary"
                        ? "bg-fmt-red text-white"
                        : "bg-gray-100 text-neutral-900 border border-gray-300"
                    }
                  `}
                >
                  {button.text}
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Add button */}
        {buttons.length < maxButtons && (
          <button
            type="button"
            onClick={handleAddButton}
            className="w-full py-2 border-2 border-dashed border-gray-300 rounded text-sm text-neutral-600 hover:border-gray-400 hover:text-neutral-700 transition-colors"
          >
            + Add Button{" "}
            {buttons.length > 0
              ? `(${maxButtons - buttons.length} remaining)`
              : ""}
          </button>
        )}
      </div>
    </div>
  );
}
